import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
import { getChargeableChipColor, getCaseStatusDisplayText, getGenderText } from '@/utils/DriverUtils'
import { DataTableHeader } from '@/models/DataTableHeader'
import { OrderMvrResponseBody, Violation, Accident, Action } from '@/models/DriverInformationModels'
import { useDisplayFormatters } from '@/composables/UseDisplayFormatters';


export default /*@__PURE__*/_defineComponent({
  __name: 'MvrSummary',
  props: {
    mvrData: { type: Object, required: false }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const props = __props

// Use the display formatters composable
const { formatDate } = useDisplayFormatters()

// Use the provided MVR data
const summaryData = computed(() => props.mvrData)

const driverName = computed(() => {
  const driverInfo = props.mvrData?.driverInfo
  if (!driverInfo) return 'Unknown Driver'
  
  const { firstName, middleInitial, lastName } = driverInfo
  const middle = middleInitial ? `${middleInitial}. ` : ''
  return `${firstName || ''} ${middle}${lastName || ''}`.trim()
})

const totalRecords = computed(() => {
  const violations = summaryData.value?.mvrActivity?.violations?.length || 0
  const accidents = summaryData.value?.mvrActivity?.accidents?.length || 0
  const actions = summaryData.value?.mvrActivity?.actions?.length || 0
  return violations + accidents + actions
})

const isViolation = (record: Violation | Accident | Action): record is Violation => {
  return record.recordType === 'Violation' || 'dmvPoints' in record
}

// Common mapping function for all record types
const mapRecordToTableItem = (record: Violation | Accident | Action, recordType: string) => {
  const points = isViolation(record) ? 
    (record.dmvPoints?.includes('/') ? 
     record.dmvPoints?.split('/')[0] : 
     record.dmvPoints || '0') : 
    0;
  
  return {
    date: record.primaryDate || '',
    recordType: record.recordType || recordType,
    fullSVC: record.standardViolation?.fullSVC || '',
    svcDescription: record.standardViolation?.description || record.nationalText || '',
    points: points,
    caseStatusIndicator: record.caseStatusIndicator || '',
    chargeable: getCaseStatusDisplayText(record.caseStatusIndicator || ''),
    convictionDate: record.normalizedConvictionDate || ''
  }
}

// Map all records using the interfaces directly - no need for separate MappedRecord interface
const allRecords = computed(() => {
  // Map violations using standardViolation properties
  const violations = summaryData.value?.mvrActivity?.violations?.map(violation => 
    mapRecordToTableItem(violation, 'Violation')
  ) || []
  
  // Map accidents using standardViolation properties  
  const accidents = summaryData.value?.mvrActivity?.accidents?.map(accident => 
    mapRecordToTableItem(accident, 'Accident')
  ) || []
  
  // Map actions using standardViolation properties
  const actions = summaryData.value?.mvrActivity?.actions?.map(action => 
    mapRecordToTableItem(action, 'Action')
  ) || []
  
  // Combine all records
  return [...violations, ...accidents, ...actions]
})

const headers = ref<DataTableHeader[]>([
  { title: 'Date', key: 'date', align: 'start' },
  { title: 'Record Type', key: 'recordType', align: 'start' },
  { title: 'SVC', key: 'fullSVC', align: 'start' },
  { title: 'SVC Description', key: 'svcDescription', align: 'start' },
  { title: 'Points', key: 'points', align: 'start' },
  { title: 'Chargeable', key: 'chargeable', align: 'start' },
  { title: 'Conviction Date', key: 'convictionDate', align: 'start' },
]);

const __returned__ = { props, formatDate, summaryData, driverName, totalRecords, isViolation, mapRecordToTableItem, allRecords, headers, get getChargeableChipColor() { return getChargeableChipColor }, get getGenderText() { return getGenderText } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})